import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Collapse,
  IconButton,
  Divider
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Science as ScienceIcon,
  CheckCircle as CheckIcon,
  LocalFlorist as CropIcon,
  Add as AddIcon
} from '@mui/icons-material';

interface FormulationElement {
  elementSymbol: string;
  targetConcentration: number;
  unit: string;
}

interface DatabaseFormulation {
  id: string;
  name: string;
  description?: string;
  cropType?: string;
  targetEc?: number;
  targetPh?: number;
  volume: number;
  volumeUnit: string;
  createdAt: string;
  updatedAt?: string;
  waterProfileId?: string;
  waterProfile?: any;
  targetElements?: {
    element?: {
      symbol: string;
      name: string;
    };
    elementSymbol?: string;
    elementName?: string;
    targetConcentration: number;
    concentration?: number;
    unit: string;
  }[];
  salts?: any[];
}

interface DatabaseFormulationSelectorProps {
  onFormulationSelect: (formulation: DatabaseFormulation | null) => void;
  onCreateNew: () => void;
  selectedFormulationId?: string;
}

const DatabaseFormulationSelector: React.FC<DatabaseFormulationSelectorProps> = ({
  onFormulationSelect,
  onCreateNew,
  selectedFormulationId
}) => {
  const [selectionMode, setSelectionMode] = useState<'existing' | 'new'>('existing');
  const [formulations, setFormulations] = useState<DatabaseFormulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFormulation, setExpandedFormulation] = useState<string | null>(null);

  // Fetch formulations from database
  useEffect(() => {
    fetchFormulations();
  }, []);

  const fetchFormulations = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/formulations?includeDetails=true');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const formulationsData = await response.json();
      console.log('Fetched formulations data:', formulationsData);

      // Extract the data array from the response
      const formulationsArray = formulationsData.data && Array.isArray(formulationsData.data)
        ? formulationsData.data
        : [];

      console.log('Formulations array:', formulationsArray);
      console.log('First formulation target elements:', formulationsArray[0]?.targetElements);

      setFormulations(formulationsArray);

      // If no formulation is selected and we have formulations, auto-select the first one
      if (!selectedFormulationId && formulationsArray.length > 0) {
        handleFormulationSelect(formulationsArray[0]);
      }
    } catch (error) {
      console.error('Error fetching formulations:', error);
      setError(`Failed to load formulations from database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const mode = event.target.value as 'existing' | 'new';
    setSelectionMode(mode);
    
    if (mode === 'new') {
      onFormulationSelect(null);
      onCreateNew();
    }
  };

  const handleFormulationSelect = (formulation: DatabaseFormulation) => {
    onFormulationSelect(formulation);
  };

  const filteredFormulations = Array.isArray(formulations) ? formulations.filter(formulation =>
    formulation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (formulation.description && formulation.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (formulation.cropType && formulation.cropType.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const toggleFormulationExpansion = (formulationId: string) => {
    setExpandedFormulation(expandedFormulation === formulationId ? null : formulationId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getElementCount = (formulation: DatabaseFormulation) => {
    const elements = formulation.targetElements || [];
    return Array.isArray(elements) ? elements.length : 0;
  };

  const getQualityIndicator = (formulation: DatabaseFormulation) => {
    const elementCount = getElementCount(formulation);
    if (elementCount === 0) return { color: 'default' as const, label: 'No Elements' };
    if (elementCount < 6) return { color: 'warning' as const, label: 'Basic' };
    if (elementCount < 10) return { color: 'info' as const, label: 'Standard' };
    return { color: 'success' as const, label: 'Comprehensive' };
  };

  const getFormulationInfo = (formulation: DatabaseFormulation) => {
    // Show target EC and pH if available, otherwise show volume
    const info = [];

    if (formulation.targetEc) {
      info.push(`EC: ${formulation.targetEc} mS/cm`);
    }

    if (formulation.targetPh) {
      info.push(`pH: ${formulation.targetPh}`);
    }

    if (formulation.volume) {
      info.push(`${formulation.volume} ${formulation.volumeUnit || 'L'}`);
    }

    return info.length > 0 ? info.join(' | ') : 'Complete Formulation';
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Crop Formulation Selection
        </Typography>
        
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Choose how to proceed:</FormLabel>
          <RadioGroup
            value={selectionMode}
            onChange={handleSelectionModeChange}
            row
          >
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label="Use Existing Formulation from Database"
            />
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Create New Formulation"
            />
          </RadioGroup>
        </FormControl>

        {selectionMode === 'existing' && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading formulations...
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                <Button onClick={fetchFormulations} sx={{ ml: 2 }}>
                  Retry
                </Button>
              </Alert>
            ) : formulations.length === 0 ? (
              <Alert severity="info">
                No formulations found in database. Create a new formulation to get started.
              </Alert>
            ) : (
              <Box>
                {/* Search Filter */}
                <TextField
                  fullWidth
                  placeholder="Search formulations by name, crop type, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Formulations Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Formulation Name</TableCell>
                        <TableCell align="center">Crop Type</TableCell>
                        <TableCell align="center">Parameters</TableCell>
                        <TableCell align="center">Elements</TableCell>
                        <TableCell align="center">Quality</TableCell>
                        <TableCell align="center">Created</TableCell>
                        <TableCell align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFormulations.map((formulation) => {
                        const qualityIndicator = getQualityIndicator(formulation);
                        const isSelected = selectedFormulationId === formulation.id;
                        const isExpanded = expandedFormulation === formulation.id;
                        
                        return (
                          <React.Fragment key={formulation.id}>
                            <TableRow
                              hover
                              selected={isSelected}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleFormulationSelect(formulation)}
                            >
                              <TableCell>
                                <Radio
                                  checked={isSelected}
                                  onChange={() => handleFormulationSelect(formulation)}
                                  value={formulation.id}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {formulation.name}
                                  </Typography>
                                  {formulation.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {formulation.description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={formulation.cropType || 'General'}
                                  color="primary"
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight="medium">
                                  {getFormulationInfo(formulation)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{getElementCount(formulation)}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={qualityIndicator.label}
                                  color={qualityIndicator.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption">
                                  {formatDate(formulation.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFormulationExpansion(formulation.id);
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expanded Details Row */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2 }}>
                                    <Grid container spacing={2}>
                                      <Grid xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Target Elements:
                                        </Typography>
                                        {formulation.targetElements && formulation.targetElements.length > 0 ? (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {formulation.targetElements.map((element, index) => {
                                              const symbol = element.element?.symbol || element.elementSymbol || 'Unknown';
                                              const concentration = element.targetConcentration || element.concentration || 0;
                                              const unit = element.unit || 'ppm';

                                              return (
                                                <Chip
                                                  key={index}
                                                  label={`${symbol}: ${concentration} ${unit}`}
                                                  variant="outlined"
                                                  size="small"
                                                />
                                              );
                                            })}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No target elements defined
                                          </Typography>
                                        )}
                                      </Grid>
                                      
                                      <Grid xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Parameters:
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Volume:</strong> {formulation.volume} {formulation.volumeUnit}
                                        </Typography>
                                        {formulation.targetEc && (
                                          <Typography variant="body2">
                                            <strong>Target EC:</strong> {formulation.targetEc} mS/cm
                                          </Typography>
                                        )}
                                        {formulation.targetPh && (
                                          <Typography variant="body2">
                                            <strong>Target pH:</strong> {formulation.targetPh}
                                          </Typography>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredFormulations.length === 0 && searchTerm && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No formulations match your search criteria.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {selectionMode === 'new' && (
          <Alert severity="info" icon={<AddIcon />}>
            <Typography variant="body2">
              You've chosen to create a new formulation. The formulation creator will be displayed below 
              where you can enter your target nutrient concentrations and save them to the database.
            </Typography>
          </Alert>
        )}

        {/* Selection Summary */}
        {selectedFormulationId && selectionMode === 'existing' && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckIcon />}>
            <Typography variant="body2">
              <strong>Selected Formulation:</strong> {formulations.find(f => f.id === selectedFormulationId)?.name}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default DatabaseFormulationSelector;
